/** @format */
import adescomStyle from '../../assets/jss/adescom/adescomStyle';
import { Dialog, DialogActions, DialogContent, DialogTitle, makeStyles, Tooltip } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '../../core/i18n';
import { useDispatch } from 'react-redux';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { configDefault } from '../../core/config';
import TariffInterface from '../../interfaces/billing/TariffInterface';
import I18n from '../../components/Translate/I18n';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import { CustomMinimalTabs } from '../../components/CustomMinimalTabs/CustomMinimalTabs';
import { BasicDataTab } from './Tabs/BasicDataTab';
import { RateTab } from './Tabs/RateTab';
import { PrefixesTab } from './Tabs/PrefixesTab';
import { HistoryTab } from './Tabs/HistoryTab';
import { LinearBillingCollectionInterface, LinearBillingInterface } from '../../interfaces/billing/LinearBillingInterface';
import Loader from '../../components/Loaders/Loader';
import { PricelistHistoryCollectionInterface } from '../../interfaces/billing/PricelistHistoryInterface';
import BasePricelistInterface from '../../interfaces/billing/BasePricelistInterface';
import RateInterface from '../../interfaces/billing/RateInterface';
import { PrefixCollectionInterface } from '../../interfaces/api/PrefixCollectionInterface';
import PrefixInterface from '../../interfaces/billing/PrefixInterface';
import { documentConstants } from '../../modules/document/actions';
import { NotificationHelper } from '../../helpers/NotificationHelper';
import moment from 'moment';
import RateRangesCustomerSummaricInterface from '../../interfaces/billing/RateRangesCustomerSummaricInterface';
import { roles, UserRoleHelper } from '../../helpers/UserRoleHelper';

const useStyle = makeStyles(adescomStyle);

interface LinearBillingEditProps {
    open: boolean;
    tariff: TariffInterface;
    selectedPricelist: BasePricelistInterface | null;
    closeDialog: (reloadData?: boolean) => void;
}

export const LinearBillingEdit = (props: LinearBillingEditProps): React.ReactElement => {
    const [initialLoad, setInitialLoad] = useState<boolean>(true);
    const [startedLoadingPricelist, setStartedLoadingPricelist] = useState<string | null>(null);
    const classes = useStyle();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(true);
    const tableStickyHeader = { root: classes.tableStickyHeader };
    const tableCellRoot = { root: classes.tableCellRoot };
    const tableStickyCommand = { root: classes.tableStickyCommand };

    const tariff = props.tariff;
    const tariffId = tariff.id;
    const propsSelectedPricelist = props.selectedPricelist;
    const [selectedPricelist, setSelectedPricelist] = useState<BasePricelistInterface | null>(null);
    const open = props.open;
    const closeDialog = props.closeDialog;

    const [linearBillingList, setLinearBillingList] = useState<LinearBillingCollectionInterface | undefined>(undefined);
    const [mainLinearBilling, setMainLinearBilling] = useState<LinearBillingInterface | undefined>(undefined);

    const [prefixes, setPrefixes] = useState<PrefixCollectionInterface | undefined>();
    const [rate, setRate] = useState<RateInterface | undefined>();
    const [selectedPrefixes, setSelectedPrefixes] = useState<Array<PrefixInterface>>([]);
    const [historyList, setHistoryList] = useState<PricelistHistoryCollectionInterface>();

    const [rateRanges, setRateRanges] = useState<RateRangesCustomerSummaricInterface[] | undefined>();
    const [usesRateRanges, setUsesRateRanges] = useState<boolean>(false);
    const [rateRangesWereLoaded, setRateRangesWereLoaded] = useState<boolean>(false);

    const itemLoader = useMemo(() => {
        return new ItemLoaders(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (!selectedPricelist) {
            return;
        }

        let filters = [
            { name: 'mainname', value: encodeURIComponent(selectedPricelist.mainname) },
            { name: 'tariffId', value: tariffId },
            { name: 'itemsPerPage', value: 999999 },
        ];

        if (selectedPricelist.historicalEvent) {
            filters.push({ name: 'activeDate', value: selectedPricelist.activeDate });
        }

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/linear_billings', configDefault.PLATFORM_API_PREFIX)),
            'PRICELIST_LIST',
            filters,
            [],
            {
                onSuccess: (data: any) => {
                    if (data['hydra:member'].length !== 0) {
                        for (let i = 0; i < data['hydra:member'].length; i++) {
                            if (!selectedPricelist?.historicalEvent) {
                                if (data['hydra:member'][i].deactivationDate === null) {
                                    setMainLinearBilling(data['hydra:member'][i]);
                                    break;
                                }
                            }
                            if (selectedPricelist?.historicalEvent) {
                                if (data['hydra:member'][i].activedate === selectedPricelist.activeDate) {
                                    setMainLinearBilling(data['hydra:member'][i]);
                                    break;
                                }
                            }
                        }
                    }
                    setLoading(false);
                    setInitialLoad(false);
                    setLinearBillingList(data);
                },
                onError: () => {
                    setLoading(false);
                    setInitialLoad(false);
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [selectedPricelist, itemLoader, tariffId]);

    useEffect(() => {
        if (!initialLoad) {
            return;
        }

        if (!propsSelectedPricelist) {
            setLinearBillingList(undefined);
            setMainLinearBilling(({
                linearBillingId: -1,
                id: -1,
                mainname: '',
                tariffId: tariffId,
                type: 0,
                strictPrefixMatch: false,
                activedate: new Date().toISOString(),
                deactivationDate: null,
            } as unknown) as LinearBillingInterface);
            setLoading(false);
            setInitialLoad(false);
            return;
        }

        let pricelistUri = propsSelectedPricelist?.historicalEvent ? '/pricelist_histories' : '/pricelists';

        itemLoader.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath(pricelistUri, configDefault.PLATFORM_API_PREFIX)),
            'PRICELIST_LIST',
            [
                {
                    name: 'customId',
                    value: encodeURIComponent(propsSelectedPricelist.customId),
                },
            ],
            [],
            {
                onSuccess: (data: any) => {
                    let found = false;
                    for (let i = 0; i < data['hydra:member'].length; i++) {
                        if (data['hydra:member'][i].customId === propsSelectedPricelist.customId) {
                            setSelectedPricelist(data['hydra:member'][i]);
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        NotificationHelper.notifyError('general:common.error_occurred', dispatch);
                        setLoading(false);
                        setInitialLoad(false);
                        closeDialog(true);
                    }
                },
                onError: () => {
                    setLoading(false);
                    setInitialLoad(false);
                    closeDialog(true);
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [propsSelectedPricelist, itemLoader, tariffId, initialLoad, dispatch, closeDialog]);

    const onClose = useCallback(
        (event: any, reason: string) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                return;
            }

            closeDialog();
        },
        [closeDialog]
    );

    const updateLinearBilling = useCallback(
        (property: string, value: any) => {
            console.log(property, value);
            let tmpLinearBilling: LinearBillingInterface;
            if (mainLinearBilling) {
                tmpLinearBilling = { ...mainLinearBilling } as LinearBillingInterface;
            } else {
                tmpLinearBilling = ({
                    linearBillingId: -1,
                    id: -1,
                    mainname: '',
                    tariffId: tariffId,
                    type: 0,
                    strictPrefixMatch: false,
                    activedate: new Date().toISOString(),
                    deactivationDate: null,
                } as unknown) as LinearBillingInterface;
            }
            if ({}.propertyIsEnumerable.call(tmpLinearBilling, property)) {
                if (property === 'type') {
                    setPrefixes(undefined);
                    if (rate || tmpLinearBilling.id !== -1) {
                        setRate(undefined);
                        NotificationHelper.notifyCustom('pricelists:lb.pricelist_type_rate_reset', dispatch, 'info');
                    }
                }
                (tmpLinearBilling as Record<string, any>)[property] = value;
                setMainLinearBilling(tmpLinearBilling);
            }
        },
        [mainLinearBilling, tariffId, rate, dispatch]
    );

    const updateRate = useCallback((rate: RateInterface) => {
        setRate(JSON.parse(JSON.stringify(rate)) as RateInterface);
    }, []);

    const updatePrefixesList = useCallback((prefixes: PrefixCollectionInterface) => {
        setPrefixes(JSON.parse(JSON.stringify(prefixes)) as PrefixCollectionInterface);
    }, []);

    const updateSelectedPrefixesList = useCallback((prefixes: Array<PrefixInterface>) => {
        setSelectedPrefixes(JSON.parse(JSON.stringify(prefixes)) as Array<PrefixInterface>);
    }, []);

    const updateHistoryList = useCallback((historyList: PricelistHistoryCollectionInterface) => {
        setHistoryList(JSON.parse(JSON.stringify(historyList)) as PricelistHistoryCollectionInterface);
    }, []);

    const updateRateRanges = useCallback((rateRanges: RateRangesCustomerSummaricInterface[]) => {
        setRateRanges(JSON.parse(JSON.stringify(rateRanges)) as RateRangesCustomerSummaricInterface[]);
    }, []);

    const updateUsesRateRanges = useCallback((usesRateRanges: boolean) => {
        setUsesRateRanges(usesRateRanges);
    }, []);

    const updateRateRangesWereLoaded = useCallback((rateRangesWereLoaded: boolean) => {
        setRateRangesWereLoaded(rateRangesWereLoaded);
    }, []);

    const loadTabs = useMemo((): any => {
        if (!mainLinearBilling) {
            return;
        }

        const tabs = [
            {
                name: i18n.t('pricelists:lb.basic_data'),
                element: <BasicDataTab linearBilling={mainLinearBilling} updateLinearBilling={updateLinearBilling} pricelist={selectedPricelist} />,
            },
            {
                name: i18n.t('pricelists:lb.rates'),
                element: (
                    <RateTab
                        linearBilling={mainLinearBilling}
                        rate={rate}
                        updateRate={updateRate}
                        rateRanges={rateRanges}
                        usesRateRanges={usesRateRanges}
                        updateRateRanges={updateRateRanges}
                        updateUsesRateRanges={updateUsesRateRanges}
                        updateRateRangesWereLoaded={updateRateRangesWereLoaded}
                    />
                ),
            },
            {
                name: i18n.t('pricelists:lb.assign_prefixes'),
                element: (
                    <PrefixesTab
                        linearBilling={mainLinearBilling}
                        pricelist={selectedPricelist}
                        prefixes={prefixes}
                        selectedPrefixes={selectedPrefixes}
                        updatePrefixesList={updatePrefixesList}
                        updateSelectedPrefixesList={updateSelectedPrefixesList}
                    />
                ),
            },
        ];

        if (!selectedPricelist?.historicalEvent) {
            tabs.push({
                name: i18n.t('pricelists:lb.history'),
                element: (
                    <HistoryTab linearBilling={mainLinearBilling} tariff={tariff} historyList={historyList} updateHistoryList={updateHistoryList} />
                ),
            });
        }

        return tabs;
    }, [
        selectedPricelist,
        mainLinearBilling,
        tariff,
        updateLinearBilling,
        prefixes,
        rate,
        selectedPrefixes,
        updatePrefixesList,
        updateRate,
        updateSelectedPrefixesList,
        historyList,
        updateHistoryList,
        updateRateRanges,
        updateUsesRateRanges,
        rateRanges,
        usesRateRanges,
        updateRateRangesWereLoaded,
    ]);

    const baseSaveChecks = useCallback((): boolean => {
        if (!selectedPricelist && (!rate || selectedPrefixes.length === 0)) {
            NotificationHelper.notifyError('pricelists:lb.pricelist_rate_prefix_required', dispatch);
            return false;
        }
        if (!mainLinearBilling) {
            NotificationHelper.notifyError('pricelists:lb.linear_billing_not_found', dispatch);
            return false;
        }
        if (
            mainLinearBilling?.deactivationDate !== null &&
            mainLinearBilling.deactivationDate !== '' &&
            mainLinearBilling.activedate !== null &&
            moment(mainLinearBilling.deactivationDate) < moment(mainLinearBilling.activedate)
        ) {
            NotificationHelper.notifyError('pricelists:lb.deactivation_date_must_be_greater_than_activation_date', dispatch);
            return false;
        }
        if (rate && rateRanges && rateRanges.length === 0 && usesRateRanges) {
            NotificationHelper.notifyError('pricelists:lb.rate_ranges_required_when_enabled', dispatch);
            return false;
        }

        return true;
    }, [selectedPricelist, mainLinearBilling, rate, selectedPrefixes, dispatch, rateRanges, usesRateRanges]);

    const add = useCallback(() => {
        if (!baseSaveChecks()) {
            return;
        }
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_billing_actions')),
            'add_linear_billing',
            {
                action: 'add_linear_billing',
                params: JSON.stringify({
                    pricelist: selectedPricelist,
                    linearBilling: mainLinearBilling,
                    rate: rate ?? null,
                    rateRanges: usesRateRanges ? rateRanges ?? [] : [],
                    usesRateRanges: usesRateRanges,
                    prefixes: selectedPrefixes,
                    tariff: tariff,
                    originSave: true,
                    rateRangesWereLoaded: rateRangesWereLoaded,
                }),
            },
            {
                onSuccess: () => {
                    closeDialog(true);
                    NotificationHelper.notifySuccess('pricelists:lb.pricelist_added', dispatch);
                },
                onError: () => {
                    //
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    }, [
        itemLoader,
        selectedPricelist,
        mainLinearBilling,
        rate,
        selectedPrefixes,
        tariff,
        closeDialog,
        baseSaveChecks,
        dispatch,
        rateRanges,
        usesRateRanges,
        rateRangesWereLoaded,
    ]);

    const save = useCallback(() => {
        if (!baseSaveChecks()) {
            return;
        }
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_billing_actions')),
            'edit_linear_billing',
            {
                action: 'edit_linear_billing',
                params: JSON.stringify({
                    pricelist: selectedPricelist,
                    linearBilling: mainLinearBilling,
                    rate: rate ?? null,
                    rateRanges: usesRateRanges ? rateRanges ?? [] : [],
                    usesRateRanges: usesRateRanges,
                    prefixes: selectedPrefixes,
                    tariff: tariff,
                    originSave: true,
                    rateRangesWereLoaded: rateRangesWereLoaded,
                }),
            },
            {
                onSuccess: () => {
                    closeDialog(true);
                    NotificationHelper.notifySuccess('pricelists:lb.pricelist_saved', dispatch);
                },
                onError: () => {
                    //
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    }, [
        itemLoader,
        selectedPricelist,
        mainLinearBilling,
        rate,
        selectedPrefixes,
        tariff,
        closeDialog,
        baseSaveChecks,
        dispatch,
        rateRanges,
        usesRateRanges,
        rateRangesWereLoaded,
    ]);

    const saveAsHistory = useCallback(() => {
        if (!baseSaveChecks()) {
            return;
        }
        itemLoader.postWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/custom_billing_actions')),
            'edit_linear_billing',
            {
                action: 'edit_linear_billing',
                params: JSON.stringify({
                    pricelist: selectedPricelist,
                    linearBilling: mainLinearBilling,
                    rate: rate ?? null,
                    rateRanges: usesRateRanges ? rateRanges ?? [] : [],
                    usesRateRanges: usesRateRanges,
                    prefixes: selectedPrefixes,
                    tariff: tariff,
                    originSave: false,
                    rateRangesWereLoaded: rateRangesWereLoaded,
                }),
            },
            {
                onSuccess: () => {
                    closeDialog(true);
                    NotificationHelper.notifySuccess('pricelists:lb.pricelist_history_saved', dispatch);
                },
                onError: () => {
                    //
                },
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
                dispatchErrorAfterCallback: true,
            }
        );
    }, [
        itemLoader,
        selectedPricelist,
        mainLinearBilling,
        rate,
        selectedPrefixes,
        tariff,
        baseSaveChecks,
        dispatch,
        closeDialog,
        rateRanges,
        usesRateRanges,
        rateRangesWereLoaded,
    ]);

    return (
        <>
            <Loader visible={loading} />
            {!initialLoad && (
                <Dialog
                    aria-labelledby="alert-dialog-slide-title"
                    aria-describedby="alert-dialog-slide-description"
                    open={open}
                    onClose={onClose}
                    maxWidth="lg"
                >
                    <DialogTitle>
                        {selectedPricelist?.historicalEvent && (
                            <>
                                {selectedPricelist && <I18n trans={'pricelists:lb.history_pricelist_edit'} />}
                                {!selectedPricelist && <I18n trans={'pricelists:lb.history_pricelist_add'} />}
                            </>
                        )}
                        {!selectedPricelist?.historicalEvent && (
                            <>
                                {selectedPricelist && <I18n trans={'pricelists:lb.pricelist_edit'} />}
                                {!selectedPricelist && <I18n trans={'pricelists:lb.pricelist_add'} />}
                            </>
                        )}
                    </DialogTitle>
                    <DialogContent>{mainLinearBilling && <CustomMinimalTabs resetActiveCard={open} tabs={loadTabs} />}</DialogContent>
                    <DialogActions>
                        <Tooltip
                            title={
                                <I18n
                                    trans={
                                        selectedPricelist?.historicalEvent
                                            ? 'pricelists:lb.save_description_history'
                                            : 'pricelists:lb.save_description'
                                    }
                                />
                            }
                        >
                            <div>
                                <CustomTextButtom
                                    callback={(): void => {
                                        if (!selectedPricelist) {
                                            add();
                                        } else {
                                            save();
                                        }
                                    }}
                                    label="pricelists:lb.save"
                                />
                            </div>
                        </Tooltip>
                        {UserRoleHelper.checkRole(roles.ROLE_PLATFORM_CUSTOM_BILLING_ACTION_HISTORICAL_EDIT) &&
                            selectedPricelist &&
                            !selectedPricelist?.historicalEvent && (
                                <Tooltip title={<I18n trans={'pricelists:lb.save_as_history_description'} />}>
                                    <div>
                                        <CustomTextButtom callback={saveAsHistory} label="pricelists:lb.save_as_history" />
                                    </div>
                                </Tooltip>
                            )}
                        <CustomTextButtom callback={closeDialog} label="general:common.close" />
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};
